import { useState, useEffect } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Handshake, CheckCircle2 } from "lucide-react";

const OPTIONAL_SECTIONS = [
  { key: "credius",  label: "Credius (pratiche legali)" },
  { key: "sinistri", label: "Sinistri" },
  { key: "report",   label: "Report periodici" },
  { key: "appalti",  label: "Gestione Appalti" },
];

/** Iter122bn · SuperAdmin · pulsante "Converti in studio" nella card di un trial. */
export default function ConvertiTrialButton({ trial, onDone }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [piani, setPiani] = useState([]);  // Iter122bu · piani dal listino
  const [form, setForm] = useState({
    nome_studio: trial.studio_nome || trial.name || "",
    report_email: trial.email || "",
    piano: "",
    sezioni_attive: [],
    n_condomini_iniziali: 0,
    p_iva: "", codice_fiscale: "",
    indirizzo: "", citta: "", cap: "", provincia: "",
    telefono: "", pec: "", iban: "",
    legale_rappresentante: "",
    data_decorrenza: new Date().toISOString().slice(0, 10),
  });

  // Iter122bu · carica piani dal listino quando si apre il dialog
  useEffect(() => {
    if (!open) return;
    api.get("/superadmin/piani").then((r) => {
      const list = (r.data || []).filter((p) => p.attivo !== false);
      setPiani(list);
      // se il form non ha piano selezionato, prendi il primo
      if (list.length > 0 && !form.piano) {
        setForm((s) => ({ ...s, piano: list[0].id }));
      }
    }).catch(() => {});
    // eslint-disable-next-line
  }, [open]);

  const toggleSez = (k) => setForm((s) => ({
    ...s, sezioni_attive: s.sezioni_attive.includes(k)
      ? s.sezioni_attive.filter((x) => x !== k)
      : [...s.sezioni_attive, k],
  }));

  const converti = async () => {
    if (!form.nome_studio.trim()) { toast.error("Nome studio obbligatorio"); return; }
    if (!form.report_email) { toast.error("Email report obbligatoria"); return; }
    if (!window.confirm(`Convertire "${trial.name}" in studio definitivo?\n\nIl tenant_id resterà invariato: tutti i condomìni, fornitori e ticket già inseriti durante la prova saranno conservati.`)) return;
    setBusy(true);
    try {
      await api.post(`/superadmin/trials/${trial.id}/converti`, form);
      toast.success(`"${form.nome_studio}" convertito in studio definitivo · email di benvenuto inviata`);
      setOpen(false);
      onDone?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore conversione"); }
    setBusy(false);
  };

  return (
    <>
      <Button variant="outline" size="sm"
              onClick={() => setOpen(true)}
              data-testid={`trial-converti-${trial.id}`}
              className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
              title="Converti in studio definitivo">
        <Handshake className="w-4 h-4" />
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-[95vw] max-w-2xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader><DialogTitle className="font-display text-lg sm:text-xl flex items-center gap-2"><Handshake className="w-5 h-5 text-brand" /> Converti trial in studio definitivo</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="p-3 rounded-lg bg-blue-50 border-2 border-blue-200 text-xs text-blue-900">
              <p className="font-semibold">Cosa succede alla conversione:</p>
              <ul className="list-disc ml-4 mt-1 space-y-0.5">
                <li>L'utente resta lo stesso (email/password invariate) · <code>account_type</code> da <code>trial</code> a <code>studio</code></li>
                <li>Il <code>tenant_id</code> resta <b>{trial.tenant_id || "trial_"+trial.id.slice(0,8)}</b> → tutti i dati sono conservati</li>
                <li>Vengono rimosse le limitazioni trial (scadenza, max condòmini, max fornitori)</li>
                <li>Viene creata la scheda dello Studio con piano e sezioni facoltative selezionate</li>
                <li>Email di benvenuto inviata a {form.report_email}</li>
              </ul>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Nome studio *</Label><Input value={form.nome_studio} onChange={(e) => setForm({ ...form, nome_studio: e.target.value })} className="h-11 mt-1" data-testid="conv-nome" /></div>
              <div><Label>Email notifiche/report *</Label><Input type="email" value={form.report_email} onChange={(e) => setForm({ ...form, report_email: e.target.value })} className="h-11 mt-1" data-testid="conv-email" /></div>
              <div>
                <Label>Piano</Label>
                <select value={form.piano} onChange={(e) => setForm({ ...form, piano: e.target.value })}
                        className="h-11 w-full mt-1 border-2 border-slate-200 rounded-md px-3" data-testid="conv-piano">
                  {piani.length === 0 && <option value="">— nessun piano configurato in Listino —</option>}
                  {piani.map((p) => {
                    const prezzo = p.modello_prezzo === "per_condominio"
                      ? `€ ${(p.prezzo_per_condominio_annuo || 0).toFixed(2)}/cond·anno`
                      : `€ ${(p.prezzo || 0).toFixed(2)}/${p.ciclo || "anno"}`;
                    return <option key={p.id} value={p.id}>{p.nome} · {prezzo}</option>;
                  })}
                </select>
              </div>
              <div><Label>N. condòmini iniziali</Label><Input type="number" value={form.n_condomini_iniziali} onChange={(e) => setForm({ ...form, n_condomini_iniziali: parseInt(e.target.value) || 0 })} className="h-11 mt-1" /></div>
              <div><Label>Data decorrenza</Label><Input type="date" value={form.data_decorrenza} onChange={(e) => setForm({ ...form, data_decorrenza: e.target.value })} className="h-11 mt-1" /></div>
              <div><Label>P.IVA</Label><Input value={form.p_iva} onChange={(e) => setForm({ ...form, p_iva: e.target.value })} className="h-11 mt-1" /></div>
            </div>

            <div>
              <Label>Sezioni facoltative da attivare</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                {OPTIONAL_SECTIONS.map((s) => (
                  <label key={s.key} className={`flex items-center gap-2 p-2 rounded border-2 cursor-pointer ${form.sezioni_attive.includes(s.key) ? "border-brand bg-brand/5" : "border-slate-200"}`}>
                    <input type="checkbox" checked={form.sezioni_attive.includes(s.key)} onChange={() => toggleSez(s.key)} data-testid={`conv-sez-${s.key}`} />
                    <span className="text-sm">{s.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="pt-2 border-t space-y-3">
              <p className="text-sm font-bold text-slate-600">Anagrafica (opzionale — utile per contratto e fatturazione)</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label>Indirizzo</Label><Input value={form.indirizzo} onChange={(e) => setForm({ ...form, indirizzo: e.target.value })} className="h-11 mt-1" /></div>
                <div><Label>Città</Label><Input value={form.citta} onChange={(e) => setForm({ ...form, citta: e.target.value })} className="h-11 mt-1" /></div>
                <div><Label>CAP</Label><Input value={form.cap} onChange={(e) => setForm({ ...form, cap: e.target.value })} className="h-11 mt-1" /></div>
                <div><Label>Provincia</Label><Input value={form.provincia} onChange={(e) => setForm({ ...form, provincia: e.target.value })} className="h-11 mt-1" /></div>
                <div><Label>Telefono</Label><Input value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} className="h-11 mt-1" /></div>
                <div><Label>PEC</Label><Input type="email" value={form.pec} onChange={(e) => setForm({ ...form, pec: e.target.value })} className="h-11 mt-1" /></div>
                <div><Label>IBAN</Label><Input value={form.iban} onChange={(e) => setForm({ ...form, iban: e.target.value })} className="h-11 mt-1" /></div>
                <div><Label>Legale rappresentante</Label><Input value={form.legale_rappresentante} onChange={(e) => setForm({ ...form, legale_rappresentante: e.target.value })} className="h-11 mt-1" /></div>
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <Button onClick={converti} disabled={busy} data-testid="conv-conferma" className="h-11 bg-emerald-600 hover:bg-emerald-700">
                <CheckCircle2 className="w-4 h-4 mr-1" /> {busy ? "Conversione…" : "Converti in studio"}
              </Button>
              <Button variant="outline" onClick={() => setOpen(false)} className="h-11">Annulla</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
